import type { Notification } from '../types';

const STORAGE_KEY = 'notification_read_status';

/**
 * お知らせモックデータ（初期状態）
 */
const initialNotifications: Notification[] = [
  {
    id: '1',
    type: 'maintenance',
    status: 'unread',
    title: 'システムメンテナンスのお知らせ',
    body: '2026年1月15日 2:00〜5:00の間、システムメンテナンスを実施します。その間サービスをご利用いただけません。ご不便をおかけしますが、ご理解いただけますようお願いいたします。',
    createdAt: '2026-01-10T10:00:00',
  },
  {
    id: '2',
    type: 'important',
    status: 'unread',
    title: '重要：利用規約改定のお知らせ',
    body: '2026年2月1日より利用規約が改定されます。主な変更点は以下の通りです。\n\n1. 個人情報の取り扱いに関する条項の追加\n2. サービス利用に関する禁止事項の明確化\n3. 免責事項の更新\n\n詳細は改定後の利用規約をご確認ください。',
    createdAt: '2026-01-09T14:30:00',
  },
  {
    id: '3',
    type: 'info',
    status: 'read',
    title: '新機能リリースのお知らせ',
    body: 'お待たせしました！新しい機能がリリースされました。\n\n【新機能】\n・ダッシュボードの表示速度が向上しました\n・お気に入り機能を追加しました\n・通知設定をカスタマイズできるようになりました\n\nぜひお試しください。',
    createdAt: '2026-01-08T09:00:00',
  },
  {
    id: '4',
    type: 'deals',
    status: 'unread',
    title: '期間限定キャンペーン実施中！',
    body: '今だけ特別割引を実施中です！\n\n【キャンペーン期間】\n2026年1月7日〜1月31日\n\n【特典内容】\n初回ご利用の方は20%OFF\n\nこの機会をお見逃しなく！',
    createdAt: '2026-01-07T12:00:00',
  },
];

/**
 * sessionStorageから既読状態を取得
 */
const getReadStatusFromStorage = (): Set<string> => {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
      return new Set(JSON.parse(stored));
    }
  } catch {
    // パースエラーは無視
  }
  return new Set();
};

/**
 * sessionStorageに既読状態を保存
 */
const saveReadStatusToStorage = (readIds: Set<string>): void => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(readIds)));
  } catch {
    // 保存エラーは無視
  }
};

/**
 * 既読IDセット（sessionStorageで永続化）
 */
let readIds: Set<string> = getReadStatusFromStorage();

/**
 * 初期状態で既読のIDを追加
 */
initialNotifications.forEach((n) => {
  if (n.status === 'read') {
    readIds.add(n.id);
  }
});

/**
 * 現在の既読状態を反映したお知らせ一覧を取得
 */
export const allNotifications: Notification[] = initialNotifications.map((n) => ({
  ...n,
  status: readIds.has(n.id) ? 'read' : 'unread',
}));

/**
 * 指定されたIDのお知らせを取得
 */
export const getNotificationById = (id: string): Notification | undefined => {
  const notification = initialNotifications.find((n) => n.id === id);
  if (notification) {
    return {
      ...notification,
      status: readIds.has(notification.id) ? 'read' : 'unread',
    };
  }
  return undefined;
};

/**
 * お知らせを既読にする（sessionStorageに永続化）
 */
export const markNotificationAsRead = (id: string): void => {
  readIds.add(id);
  saveReadStatusToStorage(readIds);
  // allNotificationsも更新
  const notification = allNotifications.find((n) => n.id === id);
  if (notification) {
    notification.status = 'read';
  }
};

/**
 * 未読件数を取得
 */
export const getUnreadCount = (): number => {
  return initialNotifications.filter((n) => !readIds.has(n.id)).length;
};
